export interface Task<I extends any[] = any[], O = unknown> {
  func: (...args: I) => Promise<O> | O
  args: I
  /** lower numbers run first (0 = highest) */
  priority?: number
}

export type TaskStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'

export interface QueuedTask<I extends any[] = any[], O = unknown> extends Task<I, O> {
  id: string
  status: TaskStatus
  promise: Promise<O>
  cancel(): void
  priority: number
  resolve: (value: O | PromiseLike<O>) => void
  reject: (reason?: unknown) => void
}

type JobQueueEvent<I extends any[] = any[], O = unknown> =
  | {
      type: 'enqueued'
      task: QueuedTask<I, O>
    }
  | {
      type: 'run'
      task: QueuedTask<I, O>
    }
  | {
      type: 'cancelled'
      task: QueuedTask<I, O>
    }
  | {
      type: 'completed'
      task: QueuedTask<I, O>
    }

type JobQueueOptions<I extends any[] = any[], O = unknown> = {
  maxConcurrency?: number
  onEvent?: (event: JobQueueEvent<I, O>) => void
}

export class JobQueue<I extends any[] = any[], O = unknown> {
  /**
   * The maximum number of tasks that can be running at once.
   */
  private maxConcurrency: number

  private tasks: QueuedTask<I, O>[] = []

  private running = 0
  private nextId = 1

  private onEvent: (event: JobQueueEvent<I, O>) => void

  constructor(options: JobQueueOptions<I, O> = {}) {
    this.maxConcurrency = options.maxConcurrency ?? 1
    this.onEvent = options.onEvent ?? (() => {})
  }

  /** enqueue a new job */
  enqueue(spec: Task<I, O>): QueuedTask<I, O> {
    const id = `t${this.nextId++}`
    const priority = spec.priority ?? Number.MAX_SAFE_INTEGER

    let resolve!: (v: O | PromiseLike<O>) => void
    let reject!: (r?: unknown) => void
    const promise = new Promise<O>((res, rej) => ((resolve = res), (reject = rej)))

    const task: QueuedTask<I, O> = {
      id,
      ...spec,
      priority,
      status: 'queued',
      promise,
      resolve,
      reject,
      cancel: () => {
        if (task.status !== 'queued') return
        task.status = 'cancelled'
        reject(new Error('Task cancelled'))
        this._finalize(task)
      }
    }

    this.tasks.push(task)
    this.tasks.sort((a, b) => a.priority - b.priority) // keep queue ordered

    this.onEvent({ type: 'enqueued', task })

    this._runNext()
    return task
  }

  /** cancel any queued tasks that match the predicate */
  cancel(predicate: (t: QueuedTask<I, O>) => boolean = () => true): number {
    const doomed = this.tasks.filter((t) => t.status == 'queued' && predicate(t))
    doomed.forEach((t) => t.cancel())
    return doomed.length
  }

  clear(): void {
    this.cancel(() => true)
  }

  hasTask(pred: (t: QueuedTask<I, O>) => boolean): boolean {
    return this.tasks.some(pred)
  }

  // ────────── internals ──────────
  private _runNext(): void {
    while (this.running < this.maxConcurrency) {
      const task = this.tasks.find((t) => t.status == 'queued')
      if (!task) break

      task.status = 'running'
      this.running++
      ;(async () => {
        try {
          this.onEvent({ type: 'run', task })
          const result = await task.func(...task.args)
          task.status = 'completed'
          task.resolve(result)
        } catch (err) {
          task.status = 'failed'
          task.reject(err)
        } finally {
          this.running--
          this._finalize(task) // ← single, delayed cleanup
        }
      })()
    }
  }
  /** emit terminal event while task is still in the queue,
   * then garbage‑collect it on the next micro‑task */
  private _finalize(task: QueuedTask<I, O>): void {
    // 1. notify subscribers
    if (task.status == 'cancelled') {
      this.onEvent({ type: 'cancelled', task })
    } else if (task.status == 'completed') {
      this.onEvent({ type: 'completed', task })
    }

    // 2. remove the task and kick the queue on the next micro‑task
    queueMicrotask(() => {
      const idx = this.tasks.indexOf(task)
      if (idx !== -1) this.tasks.splice(idx, 1)
      this._runNext()
    })
  }
}
